/**
 * Pyramide Q-sort en lecture — le rendu partagé entre la passation d'un candidat
 * et le référentiel agrégé de la campagne.
 *
 * Un seul composant pour les deux, volontairement : un référentiel affiché
 * autrement qu'une passation candidat obligerait à retraduire mentalement les
 * deux écrans avant de les comparer, alors que c'est exactement la comparaison
 * qu'on cherche à faire.
 *
 * Rendu PUR : aucune connaissance du calcul, l'appelant place déjà chaque item à
 * son niveau.
 */
import { libellesNiveauxQsort } from "@repo/utils";

export type ItemPyramide = {
  code: string;
  label: string;
  /** Niveau de la pyramide. 0 = le plus fort (haut). */
  niveau: number;
  /** Valeur affichée à droite du chip (score du candidat, moyenne référents…). */
  valeur?: string;
  /** Ligne secondaire du chip (dispersion, nombre de contributions…). */
  detail?: string;
  /** Cerne l'item : désaccord entre référents, donnée fragile. */
  alerte?: boolean;
};

/** Teintes du plus fort (niveau 0) au plus faible : la couleur porte la même
 *  information que la position, pour une lecture immédiate de la pyramide. */
const TEINTES = [
  "bg-echelle-1-surface border-echelle-1-trait text-echelle-1-contenu",
  "bg-echelle-2-surface border-echelle-2-trait text-echelle-2-contenu",
  "bg-echelle-3-surface border-echelle-3-trait text-echelle-3-contenu",
  "bg-echelle-4-surface border-echelle-4-trait text-echelle-4-contenu",
  "bg-echelle-5-surface border-echelle-5-trait text-echelle-5-contenu",
];

type Props = {
  items: ItemPyramide[];
  /** Nombre de niveaux de la pyramide (5 avec `PYRAMIDE_FIT_CULTUREL`). */
  niveaux: number;
  /** Capacité attendue par niveau. Affichée en `n/capacité` quand fournie — la
   *  passation ET le référentiel reclassé respectent tous deux les quotas, un
   *  `2/3` y signale donc un vrai manque (item non classé). */
  pyramide?: number[];
  videMessage?: string;
};

export function QsortPyramide({
  items,
  niveaux,
  pyramide,
  videMessage = "Aucun item classé.",
}: Props) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">{videMessage}</p>;
  }

  const libelles = libellesNiveauxQsort(niveaux);
  const parNiveau = Array.from({ length: niveaux }, (_, niveau) =>
    items
      .filter((item) => item.niveau === niveau)
      .sort((a, b) => a.label.localeCompare(b.label, "fr")),
  );
  const teinte = (niveau: number) =>
    TEINTES[Math.min(niveau, TEINTES.length - 1)]!;

  return (
    <div className="space-y-3">
      {parNiveau.map((itemsDuNiveau, niveau) => (
        <div key={niveau} className="space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {libelles[niveau]}
            </span>
            <span className="text-xs tabular-nums text-muted-foreground">
              {pyramide?.[niveau] !== undefined
                ? `${itemsDuNiveau.length}/${pyramide[niveau]}`
                : `${itemsDuNiveau.length} item${itemsDuNiveau.length > 1 ? "s" : ""}`}
            </span>
          </div>
          {itemsDuNiveau.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Aucun item à ce niveau.
            </p>
          ) : (
            <div className="flex flex-wrap justify-center gap-2">
              {itemsDuNiveau.map((item) => (
                <div
                  key={item.code}
                  className={`flex min-w-[8rem] max-w-[16rem] flex-1 items-start justify-between gap-2 rounded-full border px-3 py-2 text-sm ${teinte(
                    niveau,
                  )} ${item.alerte ? "ring-2 ring-etat-alerte" : ""}`}
                >
                  <span className="font-medium">
                    {item.label}
                    {item.detail && (
                      <span className="mt-0.5 block text-xs font-normal opacity-70">
                        {item.detail}
                      </span>
                    )}
                  </span>
                  {item.valeur && (
                    <span className="shrink-0 text-xs tabular-nums opacity-70">
                      {item.valeur}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
